import { getDestination } from '@/features/interactions/addToCalendar/destinations/registry';
import type { DecoratedIntegration } from '@/pages/Schedule/hooks/useIntegrations';
import { PlatformTypes } from '@/types/gql.generated';
import { hasGoogleIntegration } from '../features/interactions/addToGoogle/calendar.lib';

// Registry-first lookups with the historical fallbacks, so unregistered
// platforms keep behaving as they always have.

export const getName = (platform: PlatformTypes): string =>
  getDestination(platform)?.name ?? 'Other';

/** Compact display name for tight chip/tile layouts. */
export const getShortName = (platform: PlatformTypes): string => {
  const module = getDestination(platform);
  return module?.shortName ?? module?.name ?? 'Other';
};

/**
 * Menu-row wording where a destination deviates from its display name (the
 * Google weblink reads 'Google Calendar' to distinguish it from the direct
 * integration); defaults to the display name.
 */
export const getMenuLabel = (platform: PlatformTypes): string => {
  const module = getDestination(platform);
  return module?.menuLabel ?? module?.name ?? 'Other';
};

export const isPinnable = (platform: PlatformTypes): boolean =>
  getDestination(platform)?.isPinnable ?? true;

export const isPlatformType = (value: unknown): value is PlatformTypes => {
  return Object.values(PlatformTypes).includes(value as PlatformTypes);
};

export const ensurePlatform = (
  platform: PlatformTypes,
  integrations: DecoratedIntegration[]
): PlatformTypes => {
  if (
    platform === PlatformTypes.GoogleIntegration ||
    platform === PlatformTypes.GoogleLink
  ) {
    // Use integration if available, else fall back to link
    return hasGoogleIntegration(integrations)
      ? PlatformTypes.GoogleIntegration
      : PlatformTypes.GoogleLink;
  }
  return platform;
};

export const getPlatformAddUrl = (
  eventId: string,
  platform: PlatformTypes,
  isMobile: boolean,
  experienceId?: string
): string | null => {
  const urlKey = getDestination(platform)?.weblink?.urlKey;

  if (!urlKey) {
    return null;
  }

  const params = new URLSearchParams();
  params.append('eventId', eventId);
  params.append('device', isMobile ? 'mobile' : 'desktop');
  params.append('platform', urlKey);
  if (experienceId) {
    params.append('experienceId', experienceId);
  }

  return `/event/weblink?${params.toString()}`;
};
